/**
 * ScheduleDrawer — edit drawer for a scheduled report.
 *
 * Form fields: description, frequency, day selector, send time, reported period,
 * attachments, applied filters summary, recipients. The form cards and their state are
 * shared with the create drawer (ScheduleFormCards.jsx + useScheduleForm.js).
 *
 * Props:
 *   open, onClose, onSave, schedule (row data), frequencies,
 *   onEditFilters, saving, filterOptions (users carry user_role_weight),
 *   canManageReport, myRoleWeight
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import TimeWindowPicker from '../../../../components/v2/TimeWindowPicker.jsx';
import {
    REPORT_INTERVALS, scheduleSupportsTimeRange, scheduleSupportsTimeWindow,
} from '../api/scheduleRunHelpers.js';
import { DEFAULT_TIME_WINDOW, normalizeTimeWindow, describeTimeWindow } from '../../../../utils/reportTimeWindow.js';
import { offersCsvDetails, hasScheduleFilters } from '../../../../utils/reportTypes.js';
import { filterTermsChips, readFilterTerms, storedTimeWindow } from '../../../../utils/reportFilterTerms.js';
import { useScheduleForm, useRecipientGate, useSubmitGuard, useDrawerFocus } from './useScheduleForm.js';
import {
    ScheduleDetailsCard, ScheduleAttachmentsCard, AppliedFilters, ScheduleRecipientsCard,
} from './ScheduleFormCards.jsx';
import Drawer from '../../../../components/v2/Drawer.jsx';

const ScheduleDrawer = ({
    open,
    onClose,
    onSave,
    schedule,
    frequencies,
    onEditFilters,
    saving,
    filterOptions,
    canManageReport = true,
    myRoleWeight,
}) => {
    const form = useScheduleForm();
    const { load } = form;
    const { busy, submit } = useSubmitGuard(saving);
    /* The page merges user_role_weight into filterOptions.users, so nothing to load. */
    const gate = useRecipientGate({ open, myRoleWeight });
    const userOptions = useMemo(() => filterOptions?.users || [], [filterOptions]);
    const readOnly = !canManageReport;

    /* Reporting interval (report_interval_id 1-8) and the time-of-day window. Both were
       fixed at creation until now, which meant a scheduled report could never be
       retargeted without deleting and recreating it. */
    const [intervalId, setIntervalId] = useState(2);
    const [timeWindow, setTimeWindow] = useState({ ...DEFAULT_TIME_WINDOW });

    /* Executive and Maintenance always run over a fixed range, so neither control is
       offered for them — see scheduleRunHelpers for the full type lists. */
    const canEditInterval = scheduleSupportsTimeRange(schedule?.report_type_id);
    const canEditTimeWindow = scheduleSupportsTimeWindow(schedule?.report_type_id);
    /* From the type registry (utils/reportTypes.js). The old strict === 5 / === 10
       checks also failed whenever the API sent the id as a string. */
    const isMaintenance = !hasScheduleFilters(schedule?.report_type_id);
    const filterChips = useMemo(
        () => filterTermsChips(schedule?.report_filter_terms, filterOptions),
        [schedule?.report_filter_terms, filterOptions],
    );

    /* Populate form when drawer opens, or when a different schedule is loaded.
       Keyed on report_id rather than the schedule object on purpose: applying
       filters replaces the object (new report_filter_terms), and re-running this
       then would throw away every unsaved edit in the form, time window included. */
    useEffect(() => {
        if (open && schedule) {
            load(schedule);

            /* Anything outside 1-8 (including 0 = Custom, which a schedule can never
               legitimately hold) falls back to Yesterday — the same default
               intervalIdToRange applies, so the picker cannot show a range the query
               would not use. */
            const storedInterval = parseInt(schedule.report_interval_id, 10);
            setIntervalId(REPORT_INTERVALS.some((r) => r.id === storedInterval) ? storedInterval : 2);

            /* Absent time_window means all day, matching every schedule saved before the
               window feature existed. */
            setTimeWindow(normalizeTimeWindow(storedTimeWindow(schedule.report_filter_terms)));
        }
    }, [open, schedule?.report_id]); // eslint-disable-line react-hooks/exhaustive-deps

    useDrawerFocus(open, 'sr-description');

    const handleSubmit = useCallback(() => {
        if (busy) return;
        /* The window rides report_filter_terms, so merge rather than replace: this drawer
           does not own the other filter keys (the Filters drawer does) and must hand them
           back untouched. Reports that do not support a window are passed through
           completely unchanged, so no stray key appears in their JSON. */
        let filterTerms = schedule.report_filter_terms;
        if (canEditTimeWindow) {
            const parsed = readFilterTerms(filterTerms);
            const win = normalizeTimeWindow(timeWindow);
            filterTerms = {
                ...parsed,
                time_window: {
                    values: [win.start, win.end],
                    labels: describeTimeWindow(win.start, win.end),
                },
            };
        }

        const payload = {
            report_id: schedule.report_id,
            ...form.payloadFields(),
            /* Every selected id, including higher-ranked recipients this user may not
               remove: they stay on the schedule. */
            report_user_ids: [...form.selectedUserIds].join(','),
            report_filter_terms: filterTerms,
            /* Only sent for report types that may change it; everything else keeps its
               stored value so a pass-through save cannot retarget a fixed-range report. */
            report_interval_id: canEditInterval ? intervalId : schedule.report_interval_id,
        };
        submit(() => onSave(payload));
    }, [busy, schedule, form, canEditTimeWindow, timeWindow, canEditInterval, intervalId, submit, onSave]);

    if (!schedule) return null;

    return (
        <>
            <Drawer
                open={open}
                onClose={onClose}
                title={canManageReport ? 'Edit Scheduled Report' : 'Edit User Recipients'}
                size="lg"
                ariaLabel="Edit Scheduled Report"
                bodyProps={{ style: { overflowY: 'auto', flex: 1 } }}
                footer={(
                    <>
                        <button className="dp-btn dp-btn--cancel" onClick={onClose} disabled={busy} type="button">Cancel</button>
                        <button className="dp-btn dp-btn--primary" onClick={handleSubmit} disabled={busy || !form.description.trim()} type="button">
                            {busy ? 'Saving…' : 'Save Changes'}
                        </button>
                    </>
                )}
            >
                <ScheduleDetailsCard
                    form={form}
                    frequencies={frequencies}
                    idPrefix="sr"
                    readOnly={readOnly}
                    before={(
                        <div className="sr-form-section">
                            <span className="sr-form-section__label">Report Type</span>
                            <span style={{ fontSize: 'var(--font-size-sm)', textTransform: 'capitalize', color: 'var(--color-text-secondary)' }}>
                                {schedule.report_type_name || '--'}
                            </span>
                        </div>
                    )}
                />

                {/* ── Reported Period ──
                    The range the report covers, as distinct from when it is delivered.
                    Both values were fixed at creation until now, so retargeting a
                    schedule meant deleting and recreating it. Hidden entirely for report
                    types that always run over a fixed range. */}
                {canEditInterval ? (
                    <div className="sr-form-card">
                        <h3 className="sr-form-card__title">Reported Period</h3>

                        <div className="sr-form-section">
                            <span className="sr-form-section__label">Time Range</span>
                            {/* Presets only — a static custom date pair cannot recur, so
                                interval 0 (Custom) is never offered here. */}
                            <div className="sr-day-picker">
                                {REPORT_INTERVALS.map((range) => (
                                    <button
                                        key={range.id}
                                        type="button"
                                        className={`sr-day-chip${intervalId === range.id ? ' is-selected' : ''}`}
                                        onClick={() => setIntervalId(range.id)}
                                        disabled={!canManageReport}
                                    >
                                        {range.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {canEditTimeWindow ? (
                            <div className="sr-form-section">
                                <span className="sr-form-section__label">Time of Day</span>
                                {canManageReport ? (
                                    <TimeWindowPicker value={timeWindow} onChange={setTimeWindow} />
                                ) : (
                                    /* Read-only for users who may not manage the schedule —
                                       the picker has no disabled mode, and the value still
                                       matters to them. */
                                    <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                        {describeTimeWindow(timeWindow?.start, timeWindow?.end) || 'All day'}
                                    </span>
                                )}
                            </div>
                        ) : null}
                    </div>
                ) : null}

                <ScheduleAttachmentsCard form={form} showCsv={offersCsvDetails(schedule.report_type_id)} readOnly={readOnly}>
                    {/* Maintenance reports have no filters */}
                    {!isMaintenance ? (
                        <AppliedFilters
                            chips={filterChips}
                            action={canManageReport ? (
                                <button type="button" className="rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 10px' }} onClick={onEditFilters}>
                                    <i className="fe fe-sliders" style={{ marginRight: '4px' }} />Edit Filters
                                </button>
                            ) : <span />}
                        />
                    ) : null}
                </ScheduleAttachmentsCard>

                <ScheduleRecipientsCard form={form} users={userOptions} gate={gate} readOnly={readOnly} />
            </Drawer>
        </>
    );
};

export default ScheduleDrawer;
